import * as React from "react"
import { setActivityContext } from "./activity"
import { confirmationPresentation } from "./confirmationPresentation"
import { PRODUCT_CATALOG } from "./catalogue"
import { useConfirmationPolling } from "./hooks/useConfirmationPolling"
import type { BookingStatus as Status } from "./types"
import styles from "./confirmation.css?inline"

export type BookingConfirmationProps = {
    apiBaseUrl: string
    primaryColour: string
    surfaceColour: string
    borderRadius: number
    supportPhone: string
    supportWhatsApp: string
}

const currency = new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" })

function formatDate(value: string) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (!match) return value
    return new Intl.DateTimeFormat("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
    }).format(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
}

function formatAddress(address: Status["deliveryAddress"]) {
    if (!address) return "Delivery address unavailable"
    return [address.line1, address.line2, address.city, address.state, address.postal_code].filter(Boolean).join(", ")
}

function paymentLabel(item: Status["lineItems"][number]) {
    if (item.key === "hire") return "Hire charge"
    if (item.key === "delivery") return "Delivery"
    if (item.key === "deposit") return "Refundable deposit"
    return item.label
}

export function BookingConfirmation(props: BookingConfirmationProps) {
    const { status, error, checking, check } = useConfirmationPolling(props.apiBaseUrl)
    const [lookup, setLookup] = React.useState<Record<string, string> | null>(null)
    const [missingLookup, setMissingLookup] = React.useState(false)
    const loading = !status && !error && !missingLookup
    const view = status ? confirmationPresentation(status) : null

    React.useEffect(() => {
        if (typeof window === "undefined") return
        setActivityContext({ stage: "confirmation" })
        let token = ""
        try { token = window.sessionStorage.getItem("arctic-hire:pending-confirmation-token") || "" } catch { /* Stripe return URL is still usable */ }
        const sessionId = new URLSearchParams(window.location.search).get("session_id") || ""
        // A Stripe return link identifies its booking even if another checkout
        // has since saved a token in this tab. Keep that identity for every retry.
        const lookup: Record<string, string> | null = sessionId
            ? { checkoutSessionId: sessionId }
            : token
                ? { confirmationToken: token }
                : null
        setLookup(lookup)
        setMissingLookup(!lookup)
        if (lookup) void check(lookup)
    }, [check])

    return (
        <div className="ac-root" style={{ "--ac-primary": props.primaryColour, "--ac-surface": props.surfaceColour, "--ac-radius": `${props.borderRadius}px` } as React.CSSProperties}>
            <style>{styles}</style>
            <section className="ac-card" aria-live="polite">
                {loading && (
                    <div className="ac-loading">
                        <span />
                        <h2>Confirming your booking…</h2>
                        <p>Payment may already be complete. We’re waiting for secure confirmation.</p>
                    </div>
                )}
                {!status && (error || missingLookup) && (
                    <>
                        <div className="ac-icon ac-warning">!</div>
                        <h2>We couldn’t load the confirmation</h2>
                        <p>{missingLookup ? "This confirmation link does not contain a booking reference." : error}</p>
                        {lookup && <button className="ac-retry" type="button" disabled={checking} onClick={() => void check(lookup)}>{checking ? "Checking…" : "Check again"}</button>}
                        <Support phone={props.supportPhone} whatsapp={props.supportWhatsApp} />
                    </>
                )}
                {status && view && (
                    <>
                        <img className="ac-logo" src="/arctic-line-logo.png" alt="Arctic Hire" />
                        <div className="ac-eyebrow">
                            {view.heading}
                        </div>
                        <h2>{view.title}</h2>
                        <p className="ac-reference">Your booking reference is <strong>{status.bookingReference}</strong></p>
                        {view.outcome !== "confirmed" && <p className="ac-status-message">{view.message}</p>}
                        {!view.final && <div className="ac-status-actions">
                            {error && <p role="status">{error}</p>}
                            {checking ? <p>Checking payment status…</p> : <button className="ac-retry" type="button" onClick={() => lookup && void check(lookup)}>Check again</button>}
                        </div>}
                        <div className="ac-receipt">
                            <section className="ac-equipment" aria-label="Equipment and dates">
                                <div className="ac-product">
                                    <span className="ac-product-image">
                                        <img src={PRODUCT_CATALOG.find(product => product.slug === status.product.slug)?.image || "/arctic-line-logo.png"} alt="" />
                                        <b>{status.quantity}</b>
                                    </span>
                                    <strong>{status.quantity} x {status.product.name}</strong>
                                </div>
                                <dl className="ac-rows">
                                    <div><dt>Delivery date</dt><dd>{formatDate(status.deliveryDate)}</dd></div>
                                    <div><dt>Collection date</dt><dd>{formatDate(status.collectionDate)}</dd></div>
                                </dl>
                            </section>
                            <section className="ac-receipt-section" aria-label="Delivery address">
                                <h3>Delivery address</h3>
                                <address>{formatAddress(status.deliveryAddress)}</address>
                            </section>
                            <section className="ac-receipt-section" aria-label="Payment breakdown">
                                <h3>Payment breakdown</h3>
                                <div className="ac-payment-rows">
                                    {status.lineItems.filter((item) => item.amountPence !== 0 || item.key === "delivery").map((item) => (
                                        <div key={`${item.key}-${item.label}`}>
                                            <span>{paymentLabel(item)}</span>
                                            <strong className={item.amountPence === 0 ? "ac-free" : ""}>{item.amountPence === 0 ? "FREE" : currency.format(item.amountPence / 100)}</strong>
                                        </div>
                                    ))}
                                </div>
                                <div className="ac-paid"><span>{view.paymentLabel}</span><strong>{currency.format(status.totalPence / 100)}</strong></div>
                            </section>
                        </div>
                        <p className="ac-contact">If you have any questions about your booking, please contact us at <a href="mailto:contact@arctic-hire.co.uk">contact@arctic-hire.co.uk</a>.</p>
                    </>
                )}
            </section>
        </div>
    )
}

function Support({ phone, whatsapp }: { phone: string; whatsapp: string }) {
    if (!phone && !whatsapp) return null
    return (
        <div className="ac-support">
            {phone && <a href={`tel:${phone.replace(/\s/g, "")}`}>Call {phone}</a>}
            {whatsapp && <a href={`https://wa.me/${whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">WhatsApp</a>}
        </div>
    )
}
